/**
 * Audit timeline inline pra um app ZTNA — Lote 8 (Sub-projeto 3).
 *
 * Lista cursor-paginated de eventos vindos do endpoint
 * GET /admin/ztna/apps/{id}/audit-events. Cada linha colapsa o payload
 * por default; click expande JSON pretty-printed. Botão "Carregar mais"
 * usa next_cursor.
 */
import { useState } from 'react'
import {
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  Collapse,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core'
import { IconChevronDown, IconChevronRight, IconExternalLink } from '@tabler/icons-react'
import { useInfiniteQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { ztnaApi } from '../api'
import type { AppAuditEventItem, AppAuditEventsResponse } from '../types'

dayjs.extend(relativeTime)

interface Props {
  appId: number
  /** Para o link "auditoria global filtrada por este app" (footer secundário). */
  globalAuditHref: string
}

function severityColor(severity: string | null): string {
  switch (severity) {
    case 'error':
    case 'critical':
      return 'red'
    case 'warning':
    case 'warn':
      return 'yellow'
    case 'info':
      return 'blue'
    default:
      return 'gray'
  }
}

function actionLabel(item: AppAuditEventItem): string {
  return item.action || item.event_type || 'event'
}

function EventRow({ item }: { item: AppAuditEventItem }) {
  const [open, setOpen] = useState(false)
  const hasPayload =
    item.payload !== null
    && item.payload !== undefined
    && (typeof item.payload === 'object'
      ? Object.keys(item.payload as object).length > 0
      : true)

  return (
    <Box
      py={6}
      px={4}
      style={{
        borderLeft: '2px solid var(--mantine-color-gray-3)',
        cursor: hasPayload ? 'pointer' : 'default',
      }}
      onClick={hasPayload ? () => setOpen((v) => !v) : undefined}
    >
      <Group gap={8} wrap="nowrap" align="flex-start">
        {hasPayload ? (
          open ? (
            <IconChevronDown size={14} style={{ flexShrink: 0, marginTop: 3 }} />
          ) : (
            <IconChevronRight size={14} style={{ flexShrink: 0, marginTop: 3 }} />
          )
        ) : (
          <Box w={14} style={{ flexShrink: 0 }} />
        )}
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Badge size="xs" variant="light" color={severityColor(item.severity)}>
              {actionLabel(item)}
            </Badge>
            {item.outcome && item.outcome !== 'success' ? (
              <Badge size="xs" variant="filled" color="red">
                {item.outcome}
              </Badge>
            ) : null}
          </Group>
          <Group gap={6} wrap="nowrap">
            <Text size="xs" c="dimmed" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.actor_user_email || item.actor_user_name || '—'}
            </Text>
            <Text size="xs" c="dimmed">·</Text>
            <Text size="xs" c="dimmed" title={dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}>
              {dayjs(item.created_at).fromNow()}
            </Text>
          </Group>
          {hasPayload ? (
            <Collapse in={open}>
              <Code block style={{ fontSize: 11, maxHeight: 200, overflow: 'auto' }}>
                {JSON.stringify(item.payload, null, 2)}
              </Code>
            </Collapse>
          ) : null}
        </Stack>
      </Group>
    </Box>
  )
}

export function AuditTimeline({ appId, globalAuditHref }: Props) {
  const query = useInfiniteQuery<AppAuditEventsResponse>({
    queryKey: ['ztna', 'app-audit-events', appId],
    queryFn: ({ pageParam }) =>
      ztnaApi.listAppAuditEvents(appId, {
        limit: 20,
        before: (pageParam as string | undefined) ?? undefined,
      }),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 15_000,
  })

  const items: AppAuditEventItem[] = (query.data?.pages ?? []).flatMap((p) => p.items)

  if (query.isLoading) {
    return (
      <Group gap={8} mt="xs">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">Carregando eventos…</Text>
      </Group>
    )
  }
  if (query.error) {
    return (
      <Text size="xs" c="red" mt="xs">
        Falha ao carregar timeline. Tente recarregar.
      </Text>
    )
  }
  if (items.length === 0) {
    return (
      <Stack gap={6} mt="xs">
        <Text size="xs" c="dimmed">
          Sem eventos registrados para este app.
        </Text>
        <Anchor size="xs" href={globalAuditHref} target="_blank" rel="noopener noreferrer">
          <Group gap={4} wrap="nowrap">
            <IconExternalLink size={12} />
            <span>Auditoria global</span>
          </Group>
        </Anchor>
      </Stack>
    )
  }

  return (
    <Stack gap={2} mt={6}>
      {items.map((it) => (
        <EventRow key={it.id} item={it} />
      ))}
      <Group justify="space-between" mt={6}>
        {query.hasNextPage ? (
          <Button
            size="xs"
            variant="subtle"
            loading={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            Carregar mais
          </Button>
        ) : (
          <Text size="xs" c="dimmed">Fim da timeline.</Text>
        )}
        <Anchor size="xs" href={globalAuditHref} target="_blank" rel="noopener noreferrer">
          <Group gap={4} wrap="nowrap">
            <IconExternalLink size={12} />
            <span>Auditoria global</span>
          </Group>
        </Anchor>
      </Group>
    </Stack>
  )
}

export default AuditTimeline
